import { useEffect, useRef, useState, useMemo } from 'react';

import { Checkbox } from '@raptormaps/checkbox';
import { Table } from '@raptormaps/table';
import { Button } from '@raptormaps/button';
import { Stack } from '@raptormaps/layout';
import { CircularProgress } from '@raptormaps/progress';
import { AlertModal } from '@raptormaps/alert-modal';

import {
  useGlobalUserRoles,
  useUpdateGlobalUserRoles,
  type GlobalUserRoleUpdate,
} from '../../hooks/useRoles';
import type { OrgUser } from '../../api/types/OrgUser';

import { OpenFgaRoles, OPEN_FGA_ROLE_TO_ORG_USER } from './constants';

const tableColumns = [
  {
    name: 'Email',
    key: 'email',
  },
  {
    name: 'Raptor Admin V2',
    key: OpenFgaRoles.RaptorAdmin,
  },
  {
    name: 'Sentry Device Admin',
    key: OpenFgaRoles.SentryDeviceAdmin,
  },
  {
    name: 'Sentry Edge Provisioner',
    key: OpenFgaRoles.SentryEdgeNodeProvisioner,
  },
];

function isOpenFgaRole(role: string): role is OpenFgaRoles {
  return Object.prototype.hasOwnProperty.call(OPEN_FGA_ROLE_TO_ORG_USER, role);
}

interface NewRolesProps {
  users: OrgUser[];
  changedPermissions: GlobalUserRoleUpdate[];
  setChangedPermissions: (updates: GlobalUserRoleUpdate[]) => void;
}

export const NewRoles = ({
  users,
  changedPermissions,
  setChangedPermissions,
}: NewRolesProps) => {
  const [combinedUsers, setCombinedUsers] = useState<OrgUser[]>([]);
  const triggerAlertModalRef = useRef<() => void>(() => null);
  const userIds = useMemo(() => users.map(u => u.auth0UserId), [users]);
  const { data: fgaUsers, isLoading } = useGlobalUserRoles(userIds);
  const { mutateAsync: updateGlobalUserRoles, isLoading: isSaving } =
    useUpdateGlobalUserRoles();

  useEffect(() => {
    if (!fgaUsers || !users.length) return;

    const updatedUsers = users.map(user => {
      const fgaUser = fgaUsers?.find(fga => fga.user_id === user.auth0UserId);
      const fgaRoles = fgaUser?.object_permissions?.roles?.reduce(
        (acc, role) => {
          if (role.role_id && isOpenFgaRole(role.role_id)) {
            acc = { ...acc, [OPEN_FGA_ROLE_TO_ORG_USER[role.role_id]]: true };
          }
          return acc;
        },
        {},
      );

      return {
        ...user,
        ...fgaRoles,
      };
    });
    setCombinedUsers(updatedUsers);
  }, [users, fgaUsers]);

  const handleCheckboxChange = (
    user: OrgUser,
    fgaRole: OpenFgaRoles,
    isChecked: boolean,
  ) => {
    const update: GlobalUserRoleUpdate = {
      userId: user.auth0UserId,
      role: fgaRole,
      hasRole: isChecked,
    };
    const index = changedPermissions.findIndex(
      p => p.userId === user.auth0UserId && p.role === fgaRole,
    );
    if (index === -1) {
      setChangedPermissions([...changedPermissions, update]);
    } else {
      const updated = [...changedPermissions];
      updated[index] = update;
      setChangedPermissions(updated);
    }

    const updatedUser = {
      ...user,
      [OPEN_FGA_ROLE_TO_ORG_USER[fgaRole]]: isChecked,
    };
    setCombinedUsers(prev => {
      const idx = prev.findIndex(u => u.auth0UserId === user.auth0UserId);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = updatedUser;
      return next;
    });
  };

  const formatData = combinedUsers.map(user => ({
    email: user.email,
    [OpenFgaRoles.RaptorAdmin]: (
      <Checkbox
        checked={user.raptoradmin}
        size="small"
        style={{ marginLeft: 4 }}
        onCheckedChange={(isChecked: boolean) =>
          handleCheckboxChange(user, OpenFgaRoles.RaptorAdmin, isChecked)
        }
      />
    ),
    [OpenFgaRoles.SentryDeviceAdmin]: (
      <Checkbox
        checked={user.sentryDeviceAdmin}
        size="small"
        style={{ marginLeft: 4 }}
        onCheckedChange={(isChecked: boolean) => {
          handleCheckboxChange(user, OpenFgaRoles.SentryDeviceAdmin, isChecked);
        }}
      />
    ),
    [OpenFgaRoles.SentryEdgeNodeProvisioner]: (
      <Checkbox
        checked={user.sentryEdgeNodeProvisioner}
        size="small"
        style={{ marginLeft: 4 }}
        onCheckedChange={(isChecked: boolean) => {
          handleCheckboxChange(
            user,
            OpenFgaRoles.SentryEdgeNodeProvisioner,
            isChecked,
          );
        }}
      />
    ),
  }));

  return (
    <>
      {isLoading || !users.length ? (
        <Stack align="center" style={{ padding: 24 }}>
          <CircularProgress size={75} />
        </Stack>
      ) : (
        <Stack style={{ paddingBottom: 24 }}>
          <Table
            cols={tableColumns}
            data={formatData}
            fetchData={() => Promise.resolve()}
            totalRowCount={1}
            hideControls
          />

          <Button
            disabled={!changedPermissions.length}
            style={{ alignSelf: 'start', width: 300 }}
            onClick={() => {
              triggerAlertModalRef.current();
            }}
          >
            {changedPermissions.length
              ? 'Save Roles'
              : isSaving
              ? 'Saving...'
              : 'Saved'}
          </Button>

          <AlertModal
            title="Are you sure?"
            description="Saving will change the Raptor Admin role of the selected users. Raptor Admins have full administrative privileges, including the ability to manage settings, users, and permissions across all organizations in Raptor Maps."
            triggerRef={triggerAlertModalRef}
            onConfirm={() => {
              updateGlobalUserRoles(changedPermissions);
              setChangedPermissions([]);
            }}
            confirmBtnText="Save"
          />
        </Stack>
      )}
    </>
  );
};
